<template>
  <div class="modal baggage-modal" :class="{ 'modal--visible': visible }">
    <div class="modal__overlay" @click="$emit('close')"></div>
    <div class="modal__content baggage-modal__content" role="dialog" aria-modal="true" :aria-label="title">
      <button type="button" class="modal__close" @click="$emit('close')" aria-label="Close">&times;</button>

      <!-- Kept out of the scrolling part so the scrollbar can't reach the close button. -->
      <div class="baggage-modal__intro">
        <h3 class="baggage-modal__title">{{ title }}</h3>
        <p class="baggage-modal__subtitle">{{ "Det är ofta billigare att lägga till bagage nu än på flygplatsen." | locale }}</p>
      </div>

      <div class="baggage-modal__scroll">
        <div class="baggage-modal__panel">
          <div class="baggage-modal__panel-head">
            <span v-if="cabinBagIncluded" class="baggage-modal__panel-icon fa fa-briefcase" aria-hidden="true"></span>
            <div>
              <div v-if="cabinBagIncluded" class="baggage-modal__panel-title">{{ "Handbagage ingår" | locale }}</div>
              <div class="baggage-modal__panel-sub">{{ "Du kan ändra per resenär senare på sidan." | locale }}</div>
            </div>
          </div>

          <div v-for="passenger in passengers" :key="passenger.number" class="baggage-modal__pax">
            <div class="baggage-modal__pax-title">{{ passenger.title }}</div>

            <label
              v-for="option in passenger.options"
              :key="option.id"
              class="baggage-modal__option"
              :class="{
                'baggage-modal__option--checked': chosen[option.id],
                'baggage-modal__option--selected': option.selectedAll
              }"
            >
              <input
                type="checkbox"
                class="baggage-modal__checkbox"
                :disabled="option.selectedAll"
                v-model="chosen[option.id]"
              />
              <span class="baggage-modal__option-icon fa" :class="option.icon" aria-hidden="true"></span>
              <span class="baggage-modal__option-text">
                <span class="baggage-modal__option-title">{{ option.title }}</span>
                <span class="baggage-modal__option-price">{{ option.priceText }}</span>
                <span v-if="option.partial" class="baggage-modal__option-note">{{ partialNote }}</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="baggage-modal__footer">
        <div class="baggage-modal__actions">
          <button type="button" class="baggage-modal__btn baggage-modal__btn--ghost" @click="$emit('continue')">
            {{ continueText }}
          </button>
          <button type="button" class="baggage-modal__btn baggage-modal__btn--primary" :disabled="!hasSelection" @click="apply">
            {{ addText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global gon */
import accounting from "accounting";
import { translate } from "app/helpers";

/**
 * Whether the modal has already been shown on this page. The session takes over
 * from the next page load, this only keeps the other itineraries quiet.
 */
let modalShown = false;

/** Metadata per selectable baggage type (matches keys used in baggage.vue). */
const TYPE_META = {
  BAG1: { icon: "fa-suitcase", label: "Incheckat bagage" },
  CABIN: { icon: "fa-briefcase", label: "Handbagage" }
};
/** Render order for the bag types within a passenger. */
const TYPE_ORDER = ["BAG1", "CABIN"];
/** The standard fare baggage carries no weight of its own (as fake_baggage.vue). */
const FAKE_BAGGAGE_WEIGHT = 23;

/**
 * Orders the aggregated groups by passenger, then by bag type.
 *
 * @param {Object} left
 * @param {Object} right
 *
 * @returns {number}
 */
function compareGroups(left, right) {
  return (left.paxNumber - right.paxNumber) ||
         (TYPE_ORDER.indexOf(left.type) - TYPE_ORDER.indexOf(right.type));
}

/**
 * Sums a price field over the given ancillaries.
 *
 * @param {Array} ancillaries
 * @param {string} field
 *
 * @returns {number}
 */
function sumPrices(ancillaries, field) {
  return ancillaries.reduce((sum, ancillary) => sum + Number(ancillary[field] || 0), 0);
}

/**
 * The ancillaries that aren't picked yet, i.e. the ones still to pay for.
 *
 * @param {Array} ancillaries
 *
 * @returns {Array}
 */
function notSelected(ancillaries) {
  return ancillaries.filter((ancillary) => !ancillary.selected);
}

/**
 * Builds the rendered option out of an aggregated group.
 *
 * @param {Object} group
 *
 * @returns {Object}
 */
function buildOption(group) {
  // Allowances can differ between the flights - promise the smallest one.
  const weight = Math.min(...group.weights);
  const selectedCount = group.refs.filter((ancillary) => ancillary.selected).length;
  const price = `+${accounting.formatMoney(group.perSegment)}`;

  return {
    id: group.id,
    paxNumber: group.paxNumber,
    paxType: group.paxType,
    icon: TYPE_META[group.type].icon,
    title: `${translate(TYPE_META[group.type].label)} ${weight} kg`,
    // Prices can differ between flights, so a multi flight option shows what one
    // flight costs on average. A single flight needs no such explanation.
    priceText: group.segmentCount === 1 ? price : `${price} ${translate("per flight")}`,
    // Already picked on the itinerary, so there is nothing left to add here.
    selectedAll: selectedCount === group.refs.length,
    // Picked on some of the flights only - ticking the row covers the rest.
    partial: selectedCount > 0 && selectedCount < group.refs.length,
    segmentCount: group.segmentCount,
    addableTotal: group.addableTotal,
    refs: group.refs
  };
}

export default {
  name: "baggage-modal",
  props: {
    /**
     * Whether the modal is visible.
     */
    visible: {
      type: Boolean,
      required: true
    },
    /**
     * The itinerary. Used to read the baggage ancillaries so we can build the
     * aggregated "one checkbox per passenger and bag type" view.
     */
    itinerary: {
      type: Object,
      required: true
    },
    /**
     * Whether this itinerary offers the standard fare baggage instead of the
     * per-flight ancillaries - it has to match the baggage component the
     * itinerary template renders, or the modal would offer bags it cannot add.
     */
    fakeBaggage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /** Pending selection state, keyed by option id. Applied on "add". */
      chosen: {}
    };
  },
  computed: {
    /**
     * @returns {string}
     */
    title() {
      return translate("Har du allt bagage du behöver?");
    },
    /**
     * @returns {string}
     */
    partialNote() {
      return translate("Bagage valt för en del av flygen");
    },
    /**
     * @returns {string}
     */
    continueText() {
      return this.cabinBagIncluded
        ? translate("Fortsätt med handbagage")
        : translate("Fortsätt utan extra bagage");
    },
    /**
     * What the ticked rows will add to the trip.
     *
     * @returns {number}
     */
    selectionTotal() {
      return this.options.reduce((total, option) =>
        (!option.selectedAll && this.chosen[option.id]) ? total + option.addableTotal : total, 0);
    },
    /**
     * @returns {string}
     */
    addText() {
      if (this.selectionTotal <= 0) {
        return this.cabinBagIncluded
          ? translate("Lägg till valda bagage + handbagage")
          : translate("Lägg till valda bagage");
      }

      const amount = accounting.formatMoney(this.selectionTotal);

      return this.cabinBagIncluded
        ? translate("Lägg till valda bagage (+%{amount}) + handbagage", amount)
        : translate("Lägg till valda bagage (+%{amount})", amount);
    },
    /**
     * Whether every flight comes with a cabin bag. Then we don't sell one and
     * say so instead, so "add baggage" can't read as "you have none".
     *
     * @returns {boolean}
     */
    cabinBagIncluded() {
      const legs = this.itinerary.attributes.itinerary || [];
      if (legs.length === 0) {
        return false;
      }

      return legs.every((leg) => (leg.segments || []).every((segment) => {
        const carryOn = segment.included_carryon_baggage;

        return Boolean(carryOn) &&
               (Boolean(carryOn["BAG_COMBINED"]) ||
                Boolean(carryOn["CABIN"]) && Number(carryOn["CABIN"]["count"]) > 0);
      }));
    },
    /**
     * The bag types we sell here - a cabin bag that comes with the fare isn't
     * one of them.
     *
     * @returns {Array}
     */
    offeredTypes() {
      return this.cabinBagIncluded ? TYPE_ORDER.filter((type) => type !== "CABIN") : TYPE_ORDER;
    },
    /**
     * The options the user can tick, keeping references to the underlying
     * ancillary objects so we can toggle them all at once on confirm.
     *
     * A bag that is only sold on part of the route is not offered at all: buying
     * it would leave the traveller without baggage on the remaining flights, so
     * the whole modal is dropped instead of showing a half-covering offer.
     *
     * @returns {Array}
     */
    options() {
      const groups = this.fakeBaggage ? this.fareGroups : this.ancillaryGroups;
      const legCount = (this.itinerary.attributes.itinerary || []).length;

      if (groups.some((group) => group.segmentCount !== legCount)) {
        return [];
      }

      return groups.slice().sort(compareGroups).map(buildOption);
    },
    /**
     * Aggregates the ancillaries into one group per passenger and bag type. The
     * same bag is bought (and priced) separately for every flight, so a group
     * spans all the flights it is offered on.
     *
     * @returns {Array}
     */
    ancillaryGroups() {
      const groups = {};

      this.eachSelectableAncillary((ancillary, leg) => {
        const id = `${leg.pax_number}_${ancillary.key}`;
        const group = groups[id] || (groups[id] = {
          id,
          paxNumber: leg.pax_number,
          paxType: leg.pax_type,
          type: ancillary.key,
          weights: [],
          refs: [],
          legs: {}
        });

        group.weights.push(ancillary.weight);
        group.refs.push(ancillary);
        group.legs[leg.leg_number] = true;
      });

      return Object.keys(groups).map((id) => {
        const group = groups[id];
        const segmentCount = Object.keys(group.legs).length;

        return {
          id: group.id,
          paxNumber: group.paxNumber,
          paxType: group.paxType,
          type: group.type,
          weights: group.weights,
          segmentCount,
          // Prices can differ between flights, so show what one flight costs on
          // average - the price line says it is per flight.
          perSegment: sumPrices(group.refs, "price") / segmentCount,
          // What ticking this row actually adds to the trip: the flights that
          // aren't paid for yet.
          addableTotal: sumPrices(notSelected(group.refs), "price"),
          refs: group.refs
        };
      });
    },
    /**
     * The standard fare baggage, as one group per passenger. It covers every
     * flight at once, so we spread its price over them the same way.
     *
     * @returns {Array}
     */
    fareGroups() {
      const template = this.itinerary.attributes.template || {};
      const ancillaries = template.fake_baggage_ancillaries || {};
      const segmentCount = (this.itinerary.attributes.itinerary || []).length;

      if (segmentCount === 0) {
        return [];
      }

      return Object.keys(ancillaries)
                   .map((paxKey) => ancillaries[paxKey])
                   // Infants are not offered baggage (same as fake_baggage.vue).
                   .filter((ancillary) => ancillary.pax_type !== "INF")
                   .map((ancillary) => ({
                     id: `${ancillary.pax_number}_fare`,
                     paxNumber: ancillary.pax_number,
                     paxType: ancillary.pax_type,
                     type: "BAG1",
                     weights: [FAKE_BAGGAGE_WEIGHT],
                     segmentCount,
                     perSegment: Number(ancillary.std_price_raw_pax || 0) / segmentCount,
                     addableTotal: sumPrices(notSelected([ancillary]), "std_price_raw_pax"),
                     refs: [ancillary]
                   }));
    },
    /**
     * The options grouped per passenger, in the order they are rendered.
     *
     * @returns {Array}
     */
    passengers() {
      const passengers = [];

      this.options.forEach((option) => {
        let passenger = passengers[passengers.length - 1];

        if (!passenger || passenger.number !== option.paxNumber) {
          passenger = {
            number: option.paxNumber,
            title: this.passengerTitle(option),
            options: []
          };
          passengers.push(passenger);
        }

        passenger.options.push(option);
      });

      return passengers;
    },
    /**
     * Whether any of the options still has baggage to add - a row that is
     * already picked for every flight cannot be added again.
     *
     * @returns {boolean}
     */
    hasAddableOptions() {
      return this.options.some((option) => !option.selectedAll);
    },
    /**
     * @returns {boolean}
     */
    hasSelection() {
      return this.options.some((option) => !option.selectedAll && this.chosen[option.id]);
    }
  },
  watch: {
    visible(value) {
      // Re-read the itinerary so a re-opened modal shows the current selection.
      this.chosen = this.initialSelection();

      if (value) {
        this.reportShown();
        document.body.classList.add("modal-open");
        document.addEventListener("keydown", this.onKeydown);
      } else {
        document.body.classList.remove("modal-open");
        document.removeEventListener("keydown", this.onKeydown);
      }
    }
  },
  created() {
    this.chosen = this.initialSelection();
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);

    if (this.visible) {
      document.body.classList.remove("modal-open");
    }
  },
  methods: {
    /**
     * Whether the modal may be opened at all: it has baggage to add and the
     * traveller hasn't been asked recently.
     *
     * A method rather than a computed - "already shown" is not reactive state.
     *
     * @returns {boolean}
     */
    canOffer() {
      return this.hasAddableOptions && !modalShown && gon.baggage_modal_allowed !== false;
    },
    /**
     * Remembers that the modal has been shown: on this page directly, and for
     * the next hour through the session. Fire and forget - a lost call only
     * means one more question.
     */
    reportShown() {
      modalShown = true;
      this.$http.post("/search/baggage_modal_shown");
    },
    /**
     * Walks the selectable (not airport bought) baggage ancillaries of every
     * passenger and leg.
     *
     * @param {Function} callback Called with (ancillary, leg).
     */
    eachSelectableAncillary(callback) {
      const bagAncillaries = this.itinerary.attributes.baggage_ancillaries || {};

      Object.keys(bagAncillaries).forEach((paxKey) => {
        (bagAncillaries[paxKey] || []).forEach((leg) => {
          (leg.ancillaries || []).forEach((ancillary) => {
            if (ancillary.airport_buyable === true || !this.offeredTypes.includes(ancillary.key)) {
              return;
            }

            callback(ancillary, leg);
          });
        });
      });
    },
    /**
     * The passenger heading. Every passenger is labelled with their own type -
     * a youth is a youth, even on an itinerary priced for adults.
     *
     * @param {Object} option
     *
     * @returns {string}
     */
    passengerTitle(option) {
      return translate("Resenär %{num}, %{type}", `${option.paxNumber}`, translate(option.paxType));
    },
    /**
     * Returns the selection state to open with: what the traveller already
     * picked on the itinerary is ticked, the rest is untouched.
     *
     * @returns {Object}
     */
    initialSelection() {
      const selection = {};
      this.options.forEach((option) => {
        selection[option.id] = option.selectedAll;
      });

      return selection;
    },
    /**
     * The shared Modal helper closes any visible modal on Escape by stripping
     * the class straight off the DOM, which would leave this component's state
     * out of sync - so close through the parent instead.
     *
     * @param {KeyboardEvent} event
     */
    onKeydown(event) {
      if (event.key === "Escape") {
        this.$emit("close");
      }
    },
    /**
     * Adds the chosen bags for every passenger on the matching leg. This is
     * additive only - it never deselects baggage that was already picked on the
     * itinerary - and then asks the parent to continue to step 2.
     */
    apply() {
      this.options.forEach((option) => {
        if (!this.chosen[option.id]) {
          return;
        }

        option.refs.forEach((ancillary) => {
          ancillary.selected = true;
        });
      });

      this.$emit("apply");
    }
  }
};
</script>

<style scoped>
.baggage-modal__content {
  max-width: 620px;
  padding: 32px;
  text-align: left;
  font-family: inherit;

  /* The buttons stay put, only the baggage list scrolls. */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.baggage-modal__scroll {
  flex: 1 1 auto;
  overflow-y: auto;
}

.baggage-modal__footer {
  flex: 0 0 auto;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eceaf6;
}

.baggage-modal__intro {
  flex: 0 0 auto;
  margin-bottom: 20px;
  /* Clear of the close button, which sits in the top right corner. */
  padding-right: 24px;
}

.baggage-modal__title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
}

.baggage-modal__subtitle {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #5b5b6b;
}

.baggage-modal__panel {
  background: #f6f5fc;
  border-radius: 14px;
  padding: 16px;
}

.baggage-modal__panel-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.baggage-modal__panel-icon {
  flex: 0 0 auto;
  font-size: 18px;
  color: var(--brand, var(--border, #2311b2));
  margin-top: 2px;
}

.baggage-modal__panel-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
}

.baggage-modal__panel-sub {
  font-size: 13px;
  color: #6b6b7b;
}

.baggage-modal__pax + .baggage-modal__pax {
  margin-top: 16px;
}

.baggage-modal__pax-title {
  font-size: 13px;
  font-weight: 700;
  color: #4a4a5a;
}

.baggage-modal__option {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e6e5f0;
  border-radius: 12px;
  padding: 14px;
  margin-top: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.baggage-modal__option--checked {
  border-color: var(--brand, var(--border, #2311b2));
  box-shadow: 0 0 0 1px var(--brand, var(--border, #2311b2));
}

/* Already added for every flight - there is nothing to do in the modal. */
.baggage-modal__option--selected {
  opacity: 0.65;
  cursor: default;
}

.baggage-modal__checkbox {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin: 0;
  accent-color: var(--brand, var(--border, #2311b2));
  cursor: pointer;
}

.baggage-modal__checkbox:disabled {
  cursor: default;
}

.baggage-modal__option-icon {
  flex: 0 0 auto;
  font-size: 22px;
  color: var(--brand, var(--border, #2311b2));
  width: 26px;
  text-align: center;
}

.baggage-modal__option-text {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.baggage-modal__option-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}

.baggage-modal__option-price {
  font-size: 13px;
  color: #6b6b7b;
}

.baggage-modal__option-note {
  font-size: 12px;
  color: #b26a00;
}

.baggage-modal__actions {
  display: flex;
  gap: 12px;
}

.baggage-modal__btn {
  flex: 1 1 0;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  line-height: 1.2;
}

.baggage-modal__btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.baggage-modal__btn--ghost {
  background: #ffffff;
  border: 1px solid var(--brand, var(--border, #2311b2));
  color: var(--brand, var(--border, #2311b2));
}

.baggage-modal__btn--ghost:hover {
  background: #f6f5fc;
}

.baggage-modal__btn--primary {
  background: var(--brand, var(--border, #2311b2));
  border: 1px solid var(--brand, var(--border, #2311b2));
  color: #ffffff;
}

.baggage-modal__btn--primary:hover:not(:disabled) {
  filter: brightness(0.92);
}
</style>
